/* The .timer color shift uses a 0.4s literal (slow enough that the
 * tone escalation paces across threshold boundaries instead of
 * snapping). Not tokenized because it's a one-off duration — the
 * overlay only ships --motion-duration-fast for the 30+ snappy
 * hover/focus transitions. Revisit if a `--motion-duration-medium`
 * tier lands. */

/* Top padding is large (80px) because this is a first-run hero screen
 * — Figma 1148:562 positions the brand mark 80px from the frame edge,
 * giving the mark visual room to breathe. Tokens cap at 28px so the
 * literal stands; swap to a semantic `--hero-top` token when design
 * ships one. */
.container {
  display: flex;
  flex-direction: column;
  padding: 80px var(--spacing-16) var(--spacing-16);
  min-height: 100%;
  box-sizing: border-box;
  /* P9f: cap + center the content column. At the 380px extension side
   * panel (the design width) this is a no-op — the column is already
   * narrower than the cap. It only engages once a host gives the
   * component more room than a narrow panel: the web app's own #root
   * already caps at 500px (apps/web/src/global.css), but the extension
   * ALSO ships a resizable standalone-window mode (2026-08-01 spec)
   * with no upstream width cap, so a full-width ribbon is a real bug
   * on THIS component, not just a web layout quirk. 500px matches the
   * app-wide column cap both hosts already converge on, rather than
   * inventing a second value. */
  max-width: 500px;
  width: 100%;
  margin: 0 auto;
}

/* First-run brand moment — the Logo sits at the top of every pairing
 * state, since AppShell intentionally omits HeaderBar chrome until the
 * user is paired. The Logo primitive itself ships with zero internal
 * padding, so all of the pairing-screen breathing room lives on this
 * wrapper. */
.brandMark {
  display: flex;
  justify-content: center;
  /* 80px bottom gap mirrors the Figma 1148:562 spacing (82px between
   * the logo's bottom and the "Enter this code…" title). Tokens cap
   * at 28px so the literal stands; swap to a semantic `--hero-gap`
   * token when design ships one. */
  margin-bottom: 80px;
}

/* Single-line heading. No separate subtitle — the 6-box grid below
 * makes the "this is a pairing code" context implicit, so one focused
 * directive replaces the former two-line header+subtitle pair. */
.title {
  margin: 0 0 var(--spacing-16);
  font: var(--text-subtitle-semibold);
  color: var(--text-primary);
  text-align: center;
}

/* The main body region below the header — either the 6-box code
 * grid + status line, or a checkmark success block. Centered column
 * so every state aligns identically under the header. */
.codeArea {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-12);
}

.centered {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-12);
}

/* Visually-hidden utility. Keeps the element in the accessibility
 * tree (screen readers can read it, live regions announce changes to
 * it) while making it invisible to sighted users. Standard sr-only
 * pattern — WCAG-friendly, doesn't affect layout. Used by the
 * codeGrid's live-region content span so AT users hear state
 * transitions and the actual 6-digit pairing code. */
.srOnly {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

/* ─── Code grid ───────────────────────────────────────────────── */

/* 6 boxes per Figma 1148:562 — each is 44×48px, 4px radius, filled
 * with --button-secondary-bg-default. When the code isn't present
 * (loading/expired/error) the box renders empty; when showCode is
 * active, each cell fills with one digit. Layout stays stable across
 * state transitions so users don't see jarring reflows. */
.codeGrid {
  display: flex;
  gap: var(--spacing-8);
  align-items: center;
  justify-content: center;
}

.codeBox {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 48px;
  padding: var(--spacing-4);
  background: var(--button-secondary-bg-default);
  border-radius: var(--radius-4);
  box-sizing: border-box;
}

/* Digit glyph: Figma specs 20px/28px semibold with -0.2px tracking.
 * No composite "heading-l" token today — compose from atomic tokens. */
.codeDigit {
  font-family: var(--font-family);
  font-weight: var(--text-weight-semibold);
  font-size: var(--text-size-xl);
  line-height: var(--text-lh-xl);
  letter-spacing: -0.2px;
  color: var(--text-primary);
  /* Staggered entrance — each digit rises into place 40ms apart, so
   * the code appears as a small choreography rather than a silent
   * swap. `both` preserves the from-state before the animation
   * starts AND the to-state after it finishes. Indices are set via
   * `:nth-child` on the parent .codeBox so animation-delay scales
   * naturally with the grid position. */
  animation: digitRiseIn 220ms cubic-bezier(0.2, 0.8, 0.3, 1) both;
}

/* Selectors target nth-child positions 2–7 because the codeGrid's
 * first child is the `.srOnly` live-region text span (see index.tsx).
 * The six .codeBox elements sit at positions 2 through 7; the
 * animation-delay progression (0ms → 200ms in 40ms steps) stays the
 * same, just shifted by one selector slot. */
.codeBox:nth-child(2) .codeDigit {
  animation-delay: 0ms;
}
.codeBox:nth-child(3) .codeDigit {
  animation-delay: 40ms;
}
.codeBox:nth-child(4) .codeDigit {
  animation-delay: 80ms;
}
.codeBox:nth-child(5) .codeDigit {
  animation-delay: 120ms;
}
.codeBox:nth-child(6) .codeDigit {
  animation-delay: 160ms;
}
.codeBox:nth-child(7) .codeDigit {
  animation-delay: 200ms;
}

@keyframes digitRiseIn {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Users opting out of motion see the digits appear in place — same
 * final state, no animation. */
@media (prefers-reduced-motion: reduce) {
  .codeDigit {
    animation: none;
  }
}

/* ─── Status lines below the grid ────────────────────────────── */

.status {
  margin: 0;
  font: var(--text-micro-regular);
  letter-spacing: var(--text-xs-tracking);
  color: var(--text-secondary);
}

/* Timer tone escalates as the code's life runs out. Tertiary neutral
 * is the default; ≤60s promotes to secondary (slightly more prominent
 * against the background); ≤30s shifts to the negative-tone semantic
 * color so expiry urgency reads without extra copy. `transition`
 * smooths the threshold jump so the color shift feels paced rather
 * than abrupt. */
.timer {
  margin: 0;
  font-size: var(--text-size-s);
  color: var(--text-tertiary);
  font-variant-numeric: tabular-nums;
  transition: color 0.4s ease;
}

.timerWarning {
  color: var(--text-secondary);
}

.timerUrgent {
  color: var(--text-negative);
}

.errorText {
  margin: 0;
  font-size: var(--text-size-s);
  color: var(--text-negative);
  text-align: center;
}

/* ─── Retry row (expired / error) ────────────────────────────── */

/* The parent .codeArea is a centered flex column, so a naked child
 * hugs its content instead of stretching. This wrapper provides the
 * stretched layout hole the Button primitive needs for fullWidth to
 * actually render full-width. Margin-top matches the grid→status
 * rhythm in the rest of the state tree (spacing-4 above the row). */
.retryRow {
  margin-top: var(--spacing-4);
  align-self: stretch;
}

/* ─── Confirmed state (success checkmark) ────────────────────── */

.checkmark {
  color: var(--brand);
  display: flex;
  align-items: center;
  justify-content: center;
}

.success {
  margin: 0;
  font: var(--text-subtitle-medium);
  color: var(--brand);
}

/* ─── Install + education section (pairing-screen-education P2/P3/P7,
 * visual treatment P9) ──
 * Renders only in `loading`/`showCode` (P8) and only when the host
 * supplies BOTH `installUrl` AND `labels.installHeading` (the section's
 * master gate — fix-round finding I-1: without a heading there's no
 * framing for a lone, context-free URL, so both are required together
 * rather than `installUrl` alone); see index.tsx for the full gating
 * rationale. 380px (the extension side panel) is the design width —
 * everything below stacks in a single column and must not overflow at
 * that width. Desktop side-by-side layout, if a host wants it, is a
 * host-level concern above a breakpoint; this component ships the
 * 380px baseline (capped + centered per P9f — see `.container` above). */

/* `.codeArea`'s own `align-items: center` shrink-wraps flex children to
 * their content by default — `align-self: stretch` opts this section
 * back into the container's full width, same trick as `.retryRow`
 * above.
 *
 * P9a: this IS the card — `--bg-secondary` / `--radius-8` /
 * `--spacing-16` padding, copied from `Notification.module.css`
 * (`.notification`) and `ClipCard.module.css` (`.card`), the two
 * canonical references named by the P9 spec — not invented here. That
 * shared surface vocabulary is what makes the block read as part of
 * THIS app rather than a bolted-on afterthought, which was the root of
 * the founder's "looks super ugly" dogfood note. The card's own
 * bounded, elevated surface also satisfies P2's "visually separated"
 * requirement on its own, so the hairline divider this block used to
 * render above itself is gone (see task-4-report.md) — stacking a
 * divider on top of a card doubled up the framing — which means the
 * `margin-top` below is now the ONLY thing separating the primary
 * (code) block from the secondary (install) one. See the note on that
 * property for why its value is what it is. */
.installSection {
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-12);
  /* Founder dogfood 2026-08-07: "there should be some gap between the 2".
   * This was --spacing-16, only 4px more than the --spacing-12 gap between
   * siblings inside .codeArea — so the "deliberate section break" the
   * comment above claimed was not perceptually true, and the card read as
   * the next item in one list rather than a separate block. P9a removed
   * the hairline divider on the grounds that a card is self-separating,
   * which put the entire burden of that separation on this one value.
   * --spacing-32 is a ~2.7x step off the internal rhythm, which reads as
   * a break; --spacing-40 was rejected as too costly in the 380px side
   * panel, where vertical space is the scarce axis. */
  margin-top: var(--spacing-32);
  padding: var(--spacing-16);
  border-radius: var(--radius-8);
  background: var(--bg-secondary);
  box-sizing: border-box;
}

/* P9b: "a clear step below" `.title` (the dominant, unchanged code
 * instruction — see index.tsx). Same 14px body size as before, but
 * demoted from semibold/primary to medium/secondary so the two
 * headings on this screen read as a hierarchy instead of two
 * near-identical shouts. */
.installHeading {
  margin: 0;
  font: var(--text-body-medium);
  color: var(--text-secondary);
  text-align: center;
}

/* QR + scan hint + typed URL. Single column at the 380px design width;
 * a host may promote this to a row above a wider breakpoint (§5 of the
 * design doc calls that out as a bonus, not the basis). */
.installRow {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-8);
}

.installQr {
  flex-shrink: 0;
}

/* P9d: the QR's own frame, OUTSIDE its opaque white field — never on
 * top of it. `QrCode` paints its own white module background baked
 * into the SVG regardless of theme (see its own module.css note), so
 * this well never crops, tints, or blends into that field; it just
 * gives the image breathing room + a boundary so it stops looking like
 * "a bare white square pasted on dark" (P9d's own words). `--bg-primary`
 * is the OPPOSITE surface from the card's `--bg-secondary` (light-mode:
 * near-white card, pure-white well — a subtle raised inset; dark-mode:
 * dark-gray card, near-black well — strong contrast behind the white
 * QR), same "well/inset" pattern other cards in this app use to
 * highlight a nested block. The 1px `--stroke-neutral` border adds
 * definition in light mode, where card vs. well background alone is a
 * subtle difference. Radius matches the card's own `--radius-8` so the
 * nested shape reads as belonging to the same system. */
.installQrWell {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-8);
  border-radius: var(--radius-8);
  background: var(--bg-primary);
  border: 1px solid var(--stroke-neutral);
}

.installTextCol {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-4);
  max-width: 100%;
  text-align: center;
  /* Long install URLs must wrap inside the 380px column rather than
   * overflow it. `overflow-wrap: anywhere` is the standards-track
   * spelling (vs. the legacy `word-break: break-word` alias) — widely
   * supported in every browser this ships to. */
  overflow-wrap: anywhere;
}

.installScanHint {
  margin: 0;
  font: var(--text-micro-regular);
  color: var(--text-secondary);
}

/* P9e: reads as a URL, not a heading. Previously `--text-micro-semibold`
 * + `--text-primary` — bold enough to outweigh `.installScanHint`
 * directly above it, the opposite of the visual order (the scan
 * instruction is the primary cue; the typed URL is the fallback for
 * cameras/preferences that skip scanning). Same regular weight as the
 * scan hint now, distinguished instead by `--brand` — the same token
 * this screen already uses for its other tappable/typable affordance
 * (PR #273 established `--brand`, not `--text-link`, as this screen's
 * CTA tone). `user-select: text` is a defensive override: nothing in
 * this file sets `user-select: none` today, but this is the one
 * element whose entire purpose is "type or select me," so the intent
 * is pinned explicitly rather than relying on the inherited default. */
.installTypedUrl {
  margin: 0;
  font: var(--text-micro-regular);
  color: var(--brand);
  user-select: text;
}

/* P9b/P9c: a LABEL introducing the list below, not a third heading —
 * quiet uppercase/tracked treatment, the same demotion
 * `SettingsPanel.module.css`'s `.sectionHeader` uses for its own group
 * labels ("uppercase + tracked-out instead of ... a body-sized
 * heading, so the group label reads as ambient structure rather than
 * another line of content"). Left-aligned (not centered like the
 * heading/QR row above it) so it reads as one block with the
 * left-aligned list it introduces — the same reasoning that motivates
 * left-aligning the list itself. */
.capabilitiesHeading {
  margin: 0;
  font: var(--text-micro-medium);
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  text-align: left;
}

/* P9c: left-aligned — centered short bullet lines are the hardest
 * alignment to scan (ragged left edge on every line). Code, countdown,
 * and the card heading above stay centered per the design; this list
 * (and the label introducing it) does not. */
.capabilitiesList {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
  margin: 0;
  padding: 0;
  list-style: none;
}

.capabilitiesList li {
  margin: 0;
  font: var(--text-micro-regular);
  color: var(--text-secondary);
  text-align: left;
}

.encryptionNote {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-4);
  margin: 0;
  font: var(--text-micro-regular);
  color: var(--text-tertiary);
  text-align: center;
}

/* The Lock01 icon is a flex item beside wrapping text. Without this it
 * squashes (or distorts) once a translated `encryptionNote` is long
 * enough to compete for the row's width — several locales are ~40%
 * longer than the English string. */
.encryptionNote svg {
  flex-shrink: 0;
}
